import {
  Avatar,
  Box,
  BoxProps,
  Circle,
  Separator,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  SimpleGridProps,
  Stack,
  StackProps,
  Text,
  VStack,
  Wrap,
  Presence,
} from '@chakra-ui/react'
import {
  BreakpointContextProvider,
  useBreakpointContext,
} from '@src/components/sco/utilities/BreakpointProvider'
import { range } from 'lodash'
import React, { ReactNode } from 'react'
import { FaStar } from 'react-icons/fa6'
import { LuCheck } from 'react-icons/lu'

type SignupContainerProps = {
  children?: any
  contentProps?: SignupContainerContentProps
  renderContent?: (props?: any) => ReactNode
  productId?: string
  maxWidth?: string
  containerProps?: BoxProps
  contentContainerProps?: BoxProps
}

export const SignupContainer = (props: SignupContainerProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  return (
    <Box
      bgColor={'colorPalette.subtle'}
      borderRadius={'xl'}
      boxShadow={'2xl'}
      maxWidth={props.maxWidth || '1420px'}
      width={'full'}
      {...props.containerProps}
    >
      <Stack
        align={'stretch'}
        direction={getBreakpointValue(['column', 'column', 'row-reverse'])}
        gap={0}
        justify={'stretch'}
      >
        <Box
          bgColor={'colorPalette.muted/50'}
          borderRightRadius={[null, null, 'lg']}
          flex={[1, 1, 1, 1, 4]}
          padding={{ base: '0', md: '1em', lg: '2em' }}
          {...props.contentContainerProps}
        >
          <BreakpointContextProvider>
            <Presence
              animationDuration={'moderate'}
              present={true}
              animationName={{
                _open: 'slide-from-bottom, fade-in',
                _closed: 'slide-to-bottom, fade-out',
              }}
            >
              {props.children}
            </Presence>
          </BreakpointContextProvider>
        </Box>
        <Box flex={[1, 1, 1, 1, 6]}>
          <BreakpointContextProvider>
            {props.renderContent ? (
              props.renderContent()
            ) : (
              <SignupContainerContent productId={props.productId} {...(props.contentProps || {})} />
            )}
          </BreakpointContextProvider>
        </Box>
      </Stack>
    </Box>
  )
}

export type SignupContainerContentProps = {
  dividerLabel?: ReactNode
  featureItems?: ReactNode[]
  hideQuotes?: boolean
  quotes?: Array<QuoteProps>
  quotesGridProps?: SimpleGridProps
  productId?: string
  subtitle?: ReactNode
  title?: ReactNode
}

export const SignupContainerContent = (props: SignupContainerContentProps) => {
  const featureItems =
    props.featureItems || productFeatures[props.productId || 'default'] || productFeatures.default

  return (
    <>
      <VStack
        align={'stretch'}
        gap={'1.25em'}
        paddingX={['1em', '1em', '2em', '3em']}
        paddingY={['2em', '2em', '2em', '3em']}
        width={'full'}
      >
        <Box>
          <Heading fontSize={['lg', 'lg', 'xl', '1.6em']} lineHeight={'1.33'}>
            {props.title ||
              productTitles[props.productId] ||
              'Join our community of thousands of Founders, Advisors, and Experts to take your startup to the next level.'}
          </Heading>

          {props.subtitle}
        </Box>

        <SectionDivider
          label={
            props.dividerLabel || (props.productId ? <>What's&nbsp;Included</> : <>What's&nbsp;Available</>)
          }
        />

        <FeatureChecklist items={featureItems} />

        {!props.hideQuotes && (
          <SectionDivider label={<>{productQuoteTitles[props.productId] || 'What Members are Saying'}</>} />
        )}

        {!props.hideQuotes && (
          <Quotes
            gridProps={props.quotesGridProps}
            quotes={
              productQuotes[props.productId] ||
              props.quotes || [
                {
                  quoteText:
                    '"The worst thing you can do as a Founder is go it alone. This is the missing piece of the puzzle - a tribe that you can grow with."',
                  person: {
                    name: 'Dan Martell',
                    tagline: '5x founder w/ 3 exits. Coach to high performing SaaS Founders + Investor',
                    pictureUrl:
                      'https://dq2uhp934kg74.cloudfront.net/production/20240515-0341/img/4faaf8ded3aea655e630df805bfb4062/dan-martell.jpg',
                  },
                },
                {
                  quoteText:
                    '"The best part of being in a Founder Group is seeing how other Founders are solving similar problems. It\'s like seeing every alternate ending of your own story."',
                  person: {
                    name: 'Marc Nager',
                    tagline: 'Greater Colorado Venture Fund, Former Co-Founder of Startup Weekend',
                    pictureUrl:
                      'https://dq2uhp934kg74.cloudfront.net/production/20240515-0341/img/7458c68afd742d3023863e8b6c87b8ba/marc-nager-2.jpg',
                  },
                },
              ]
            }
          />
        )}
      </VStack>
    </>
  )
}

const SectionDivider = (props: { label: any }) => {
  return (
    <HStack paddingY={'0.5em'} width={'full'}>
      <Text
        fontSize={'0.8em'}
        opacity={0.67}
        paddingRight={'1em'}
        textStyle={'badgeSm'}
        whiteSpace={'nowrap'}
      >
        {props.label}
      </Text>
      <Separator flexGrow={1} />
    </HStack>
  )
}

const Quotes = (props: { quotes: Array<QuoteProps>; gridProps?: SimpleGridProps }) => {
  const { getBreakpointValue } = useBreakpointContext()

  return (
    <SimpleGrid columns={getBreakpointValue([1, 1, 2])} gap={'1em'} {...props.gridProps}>
      {props.quotes.map((quote, index) => (
        <Quote key={index} {...quote} />
      ))}
    </SimpleGrid>
  )
}

type QuoteProps = {
  stackProps?: StackProps
  stars?: number
  quoteText: any
  person: {
    name?: string
    pictureUrl?: string
    tagline?: string
  }
}

const Quote = (props: QuoteProps) => {
  const { getBreakpointValue } = useBreakpointContext()

  return (
    <VStack
      align={'stretch'}
      background={'colorPalette.fg/20'}
      borderRadius={'lg'}
      gap={3}
      justify={'stretch'}
      padding={4}
      {...props.stackProps}
    >
      {props.stars && (
        <HStack gap={1} marginBottom={1}>
          {range(0, props.stars).map(i => (
            <Icon key={i} asChild color={'orange'} opacity={0.75}>
              <FaStar key={i} />
            </Icon>
          ))}
        </HStack>
      )}
      <Text color={'fg'} flex={1} fontStyle={'italic'}>
        {props.quoteText}
      </Text>
      {props.person && (
        <HStack>
          {props.person.pictureUrl ? (
            <>
              <Avatar.Root>
                <Avatar.Fallback />
                <Avatar.Image src={props.person.pictureUrl} />
              </Avatar.Root>
              <Box>
                <Heading color={'fg/80'} fontSize={'sm'} lineHeight={'shorter'}>
                  {props.person.name}
                </Heading>
                <Text color={'fg/80'} fontSize={'0.75em'}>
                  {props.person.tagline}
                </Text>
              </Box>
            </>
          ) : (
            <Wrap direction={getBreakpointValue(['column', 'column', 'row'])} gap={1}>
              <Heading color={'colorPalette.fg/70'} fontSize={'sm'}>
                {'– '}
                {props.person.name}
              </Heading>
              <Text color={'colorPalette.fg/60'} fontSize={'0.75em'}>
                {props.person.tagline}
              </Text>
            </Wrap>
          )}
        </HStack>
      )}
    </VStack>
  )
}

const FeatureChecklist = (props: { items: Array<ReactNode> }) => {
  const { getBreakpointValue } = useBreakpointContext()
  return (
    <SimpleGrid columns={1} gap={'1em'} width={'full'}>
      {props.items.map((item, index) => (
        <HStack key={index} align={'flex-start'} gap={'0.75em'} justify={'stretch'} width={'full'}>
          <Box>
            <Circle bgColor={'colorPalette.border'} lineHeight={0} padding={'0.125em'}>
              <Box padding={'0.1em 0.05em 0.05em 0.05em'}>
                <Icon asChild boxSize={'0.75em'} margin={'0.125em'}>
                  <LuCheck />
                </Icon>
              </Box>
            </Circle>
          </Box>
          <Box fontFamily={'Poppins, sans-serif'} width={'full'}>
            {item}
          </Box>
        </HStack>
      ))}
    </SimpleGrid>
  )
}

const productTitles = {
  'scom-pitch-deck-audit': 'Level-Up Your Pitch Deck',
}

const productQuoteTitles = {
  'scom-pitch-deck-audit': 'What You Get Back',
}

const productQuotes = {
  'scom-pitch-deck-audit': [
    {
      quoteText:
        'Every slide, graded. A clear Strong / Needs Work / Fundamental Rework verdict on all nine sections, so you know exactly where to focus.',
    },
    {
      quoteText:
        'The fixes, written for you. Each weak slide comes back rewritten, paste-ready copy you drop straight into your deck.',
    },
  ],
}

const productFeatures = {
  default: [
    <Text key={0}>Work directly with Expert Startups.com Advisors</Text>,
    <Text key={1}>Weekly check-ins and milestone development sessions</Text>,
    <Text key={2}>Self-guided online courses to launch and grow your startup</Text>,
    <Text key={3}>Access to daily workshops, community events, and office hours</Text>,
    <Text key={4}>Members-only online discussion forums and live chat</Text>,
    <Text key={5}>Membership in a global network of Founders</Text>,
    <Text key={6}>Over $25,000 in Partner Perks.</Text>,
    <Text key={7}>Verified Investor Directory</Text>,
  ],
  'scom-pitch-deck-audit': [
    <Text key={0}>
      A full, slide-by-slide audit of your pitch deck, graded against the framework Wil Schroter built
    </Text>,
    <Text key={1}>
      An investor's-eye overview plus a 9-category scorecard: Problem, Solution, Market, Traction,
      Competition, Team, and more
    </Text>,
    <Text key={2}>Clear, specific notes on what works and what needs work on every slide</Text>,
    <Text key={3}>
      A copy-paste rewrite for each slide that needs one, delivered as a PDF in under 24 hours
    </Text>,
  ],
  'scom-accelerator-basic': [
    <Text key={0}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={1}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={2}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={3}>Membership in a global network of Founders</Text>,
  ],
  'scom-accelerator-community': [
    <Text key={0}>Access to all daily workshops and community events</Text>,
    <Text key={1}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
    <Text key={2}>Directly contact up to 10 members per day</Text>,
    <Text key={3}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={4}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={5}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={6}>Membership in a global network of Founders</Text>,
  ],
  'scom-accelerator-tools': [
    <Text key={0}>Publish Goals to find funding, customers, co-founders, and more.</Text>,
    <Text key={1}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={2}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={3}>Send 3 investor outreach emails per day, with click tracking.</Text>,
    <Text key={4}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
    <Text key={5}>Directly contact up to 10 members per day</Text>,
    <Text key={6}>Access to daily workshops and community events</Text>,
    <Text key={7}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={8}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={9}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={10}>Membership in a global network of Founders</Text>,
  ],
  'scom-accelerator-advisor': [
    <Text key={0}>Publish Goals to find funding, customers, co-founders, and more.</Text>,
    <Text key={1}>Work directly with Expert Startups.com Advisors to launch and grow your startup</Text>,
    <Text key={2}>Weekly check-ins and milestone development sessions</Text>,
    <Text key={3}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={4}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={5}>15 investor outreach emails per day, with click tracking.</Text>,
    <Text key={6}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={7}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={8}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={9}>Membership in a global network of Founders</Text>,
    <Text key={10}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
    <Text key={11}>Directly contact up to 10 members per day</Text>,
    <Text key={12}>Access to all workshops and community events</Text>,
  ],
  'scom-accelerator-community-marketing-sprint': [
    <Text key={0}>
      <strong>2x/week LIVE Marketing Workshops</strong> on Mondays & Fridays, no slides, pure implementation.
    </Text>,
    <Text key={1}>
      <strong>Hands‑on feedback & live campaign launches</strong> every session.
    </Text>,
    <Text key={2}>
      <strong>24/7 Slack + AI Prompt Library</strong> for “I’m-stuck‑at‑midnight” support.
    </Text>,
    <Text key={3}>
      <strong>50+ bite‑size Playbooks & Checklists</strong> to keep you moving between sessions.
    </Text>,
    <Text key={4}>
      <strong>$25K+ in Partner Perks</strong> on growth tools and services.
    </Text>,
    <Text key={5}>
      <strong>Monthly Channel Experts</strong>: TikTok ads, Cold‑email, Landing‑page UX, and more.
    </Text>,
  ],
  'scom-accelerator-advisor-investor-matching': [
    <Text key={0}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={1}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={2}>15 investor outreach emails per day, with click tracking.</Text>,
    <Text key={3}>AI-powered pitch builder to generate individualized messages for each investor.</Text>,
    <Text key={4}>1-on-1 office hours with advisors and experts.</Text>,
    <Text key={5}>Access to all expert-led workshops and community events.</Text>,
    <Text key={6}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={7}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={8}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={9}>Membership in a global network of Founders</Text>,
    <Text key={10}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
  ],
  'scom-accelerator-investor-matching-pro': [
    <Text key={0}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={1}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={2}>30 investor outreach emails per day, with click tracking.</Text>,
    <Text key={3}>AI-powered pitch builder to generate individualized messages for each investor.</Text>,
    <Text key={4}>1-on-1 office hours with advisors and experts.</Text>,
    <Text key={5}>Access to all expert-led workshops and community events.</Text>,
    <Text key={6}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={7}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={8}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={9}>Membership in a global network of Founders</Text>,
    <Text key={10}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
  ],
  'scom-accelerator-advisor-investor-matching-pro': [
    <Text key={0}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={1}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={2}>15 investor outreach emails per day, with click tracking.</Text>,
    <Text key={3}>AI-powered pitch builder to generate individualized messages for each investor.</Text>,
    <Text key={4}>1-on-1 office hours with advisors and experts.</Text>,
    <Text key={5}>Access to all expert-led workshops and community events.</Text>,
    <Text key={6}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={7}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={8}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={9}>Membership in a global network of Founders</Text>,
    <Text key={10}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
  ],
  'scom-accelerator-advisor-investor-matching-pro-one': [
    <Text key={0}>Lifetime access to the Investor Matching and Outreach Tools.</Text>,
    <Text key={1}>1 Year of Complimentary Access to Advisor Plan</Text>,
    <Text key={2}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={3}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={4}>30 investor outreach emails per day, with click tracking.</Text>,
    <Text key={5}>Concierge support and pitch deck review.</Text>,
    <Text key={6}>AI-powered pitch builder to generate individualized messages for each investor.</Text>,
    <Text key={7}>Access to all expert-led workshops, community events, and office hours.</Text>,
    <Text key={8}>Unlimited access to all Guided Courses and Video Series</Text>,
    <Text key={9}>Over $25,000 in Partner Perks on powerful startup tools.</Text>,
    <Text key={10}>Unlimited access to Bizplan.com, Fundable.com, and Launchrock.com</Text>,
    <Text key={11}>Membership in a global network of Founders</Text>,
    <Text key={12}>Member Matching to find talent, co-founders, peers, and mentors</Text>,
  ],
  'scom-accelerator-investor-matching': [
    <Text key={0}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={1}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={2}>Send 15 investor outreach emails per day, with click tracking.</Text>,
    <Text key={3}>AI-powered pitch builder to generate individualized messages for each investor.</Text>,
  ],
  'scom-accelerator-advisor-investor-outreach': [
    <Text key={0}>
      <strong>Concierge Service</strong> to manage your matches and outreach for you.
    </Text>,
    <Text key={1}>Unlimited investor matches, curated specifically for your fundraise.</Text>,
    <Text key={2}>Over 10,000 verified investor profiles with contact information.</Text>,
    <Text key={3}>Send 15 investor outreach emails per day, with click tracking.</Text>,
    <Text key={4}>AI-powered pitch builder to generate individualized messages for each investor.</Text>,
  ],
}
